import { Injectable, NgZone, Injector } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFirestore } from '@angular/fire/firestore';
import firebase from 'firebase/app';
import 'firebase/auth';
import { Observable, from, of, BehaviorSubject } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { shareReplay } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { cfRegionLinks } from '../core/helper/cfRegionLinks';
import { AlertController } from '@ionic/angular';
import { API_KEY_EXPIRY_MARGIN_MS, expiryToPersist, isKeyFresh } from './api-key-expiry';
import { isPartnerLaunch, partnerLaunchEnv } from '../ars-embed/partner-launch-sandbox';
import { PARTNER_SESSION_ENDED_EVENT, partnerApiBase, partnerEmbedRequest } from '../ars-embed/partner-launch';

export interface User {
  created: any;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  windowRef: any;
  currentUser: any;
  userId = "";
  hasVerifiedEmail = false;
  prefix: any;
  line: any;
  verifCode: any;
  user: Observable<User>;
  public user$: Observable<any> = this.afAuth.user.pipe(shareReplay(1));
  Useremail = "";
  userData: any;
  userRole = "";
  private data = new BehaviorSubject<string>('');
  customerNumber = 0;
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  accountId: string = "";
  // user Plan with behaviur subject
  private userPlan = new BehaviorSubject<string>('');
  userPlan$ = this.userPlan.asObservable();
  private licenses = 0;
  private apiKey: string = '';

  // Storage keys for the session api key and the expiry the server hands back when minting it.
  private static readonly API_KEY_KEY = 'apiKey';
  private static readonly API_KEY_EXPIRY_KEY = 'apiKeyExpiresAt';
  // In-flight mint, shared by every caller that asks while it runs (see mintApiKey).
  private apiKeyMint: Promise<string | null> | null = null;

  customToken = "";

  constructor(
    private afAuth: AngularFireAuth,
    private db: AngularFirestore,
    private router: Router,
    private ngZone: NgZone,
    private http: HttpClient,
    private injector: Injector,
    private alertCtrl: AlertController,
  ) {
    // firebase.auth().useEmulator('http://localhost:9099/');
    // AngularFireAuth handles persistence automatically
    // Firebase persistence is set to LOCAL by default

    // Load custom token from localStorage if available
    const storedToken = localStorage.getItem('customerCustomToken');
    // Clean up invalid tokens (e.g., "[object Object]")
    if (storedToken && (storedToken === '[object Object]' || storedToken.includes('[object'))) {
      console.warn('[Auth] Clearing invalid stored token');
      localStorage.removeItem('customerCustomToken');
      this.customToken = '';
    } else {
      this.customToken = storedToken || '';
    }

    // userPlan is set exclusively by saveUserPlan() after server validation during login

    
    this.user$.subscribe(user => {
      const coolApp = firebase.apps.find(a => a.name === 'coolstore');
      if (!coolApp) return;
      if (user) {
        //needed because now coolstore needs auth. and this Auth and coolstore auth is different. Hence updating it here
        coolApp.auth().updateCurrentUser(user)
          .catch(err => console.error('[Auth] Failed to mirror user onto coolstore app', err));
      } else {
        coolApp.auth().signOut().catch(() => { /* already signed out */ });
      }
    });
    this.user = this.afAuth.authState.pipe(
      switchMap(user => {
        if (user) {
          this.currentUser = user.uid;
          console.log("this.currentUser", this.currentUser);
          this.persistAuthState(user);
          return this.db.doc(`customers/${user.uid}`).valueChanges();
        } else {
          return of(null);
        }
      })
    )
  }

  getCustomToken() {
    if (!this.customToken) {
      const storedToken = localStorage.getItem('customerCustomToken');
      // Clean up invalid tokens
      if (storedToken && (storedToken === '[object Object]' || storedToken.includes('[object'))) {
        console.warn('[Auth] getCustomToken: Clearing invalid stored token');
        localStorage.removeItem('customerCustomToken');
        this.customToken = '';
      } else {
        this.customToken = storedToken || '';
      }
    }
    return this.customToken;
  }

  async showPopUpPasswordAsk(email: string, credential: firebase.auth.AuthCredential) {
    const alert = await this.alertCtrl.create({
      header: 'Login Error',
      message: 'Seems like you already have an account using your email address. Please enter your password to link your accounts.',
      inputs: [
        {
          name: 'password',
          type: 'password',
          placeholder: 'Password'
        }
      ],
      buttons: [
        {
          text: 'Abbrechen',
          role: 'cancel'
        },
        {
          text: 'Bestätigen',
          handler: async (data) => {
            const password = data.password;
            return password;
          }
        }
      ]
    })

    await alert.present();
    return alert.onDidDismiss();

  }

  async microsoftLogin() {
    let response;
    try {
      response = await this.afAuth.signInWithPopup(new firebase.auth.OAuthProvider('microsoft.com'));
    } catch (error) {
      console.error('Microsoft login failed:', error);
      if (error.code === 'auth/account-exists-with-different-credential') {
        const email = error.email;
        const methods = await this.afAuth.fetchSignInMethodsForEmail(email);
        await this.showPopUpPasswordAsk(email, error.credential);

      }
    } finally {
      return response;
    }
  }

  async checkAndAutoLogin(): Promise<boolean> {
    try {
      const currentUser = await this.afAuth.authState.pipe(take(1)).toPromise();
      if (!currentUser) {
        return false;
      }
      this.isAuthenticatedSubject.next(true);
      return true;
    } catch {
      return false;
    }
  }

  private persistAuthState(user: any) {
    if (user) {
      const authData = {
        uid: user.uid,
        email: user.email,
        lastAuth: new Date().toISOString()
      };
      localStorage.setItem('monaAuthCache', JSON.stringify(authData));
    }
  }

  hasPersistedAuth(): boolean {
    const authCache = localStorage.getItem('monaAuthCache');
    if (!authCache) return false;

    try {
      const authData = JSON.parse(authCache);
      const lastAuth = new Date(authData.lastAuth);
      const now = new Date();
      const hoursDiff = (now.getTime() - lastAuth.getTime()) / (1000 * 60 * 60);

      return hoursDiff < 24;
    } catch {
      return false;
    }
  }

  clearPersistedAuth() {
    localStorage.removeItem('monaAuthCache');
  }

  private async handleCustomToken(customToken: any): Promise<void> {
    this.setCustomTokenObj(customToken);
    const tokenStr = typeof customToken === 'string' ? customToken : (customToken && customToken.customToken);
    if (tokenStr && typeof tokenStr === 'string') {
      try {
        await this.loginWithToken(tokenStr);
      } catch (err) {
        console.error('Failed to sign in with custom token:', err);
      }
    } else {
      console.error('Invalid customToken format from backend');
    }
  }


  // getIdToken() {
  //   // let token = this.afAuth.idToken
  //   // return token;
  // }

  async getIdToken(): Promise<string | null> {
    try {
      const user = await this.afAuth.currentUser;
      if (!user) return null;
      return await user.getIdToken();
    } catch (e) {
      return null;
    }
  }

  getCurrentUserFromFirebaseAuth() {
    return firebase.auth().currentUser;
  }

  async waitForAuthState(): Promise<firebase.User | null> {
    // Wait for the first emission from authState
    return this.afAuth.authState.pipe(take(1)).toPromise();
  }

  returnUser() {
    return this.Useremail
  }

  saveEmail(email) {
    this.Useremail = email;
  }




  returnLicenses() {
    return this.licenses;
  }


  // The platform mints SESSION api keys with a fixed server-side lifetime, and the mint response
  // carries the expiry. Storing only the key - as this used to - makes a dead key indistinguishable
  // from a live one, so the regeneration path below was only ever reached when the cache was EMPTY,
  // never when it was STALE: a user logged in past the key's lifetime kept sending a key the server
  // had already stopped accepting, and nothing re-minted or told them.
  async returnApiKey(): Promise<string | null> {
    const cachedApiKey = localStorage.getItem(AuthService.API_KEY_KEY);
    if (cachedApiKey && this.isApiKeyFresh()) {
      this.apiKey = cachedApiKey;
      return this.apiKey;
    }
    if (cachedApiKey) {
      // Past its lifetime. Drop it rather than send a key the server will reject - a rejected key
      // surfaces as a 401 that several call sites swallow, which is what hid this in the first place.
      this.clearApiKey();
    }
    return this.mintApiKey();
  }

  // Whether the cached key is still inside its server-issued lifetime. A key with NO recorded expiry
  // counts as stale: keys stored before the expiry became a persisted field carry no stamp, and
  // reusing one is exactly the failure this guards against. The cost of being wrong here is one
  // extra mint, which is cheap and safe (see mintApiKey).
  isApiKeyFresh(): boolean {
    return isKeyFresh(localStorage.getItem(AuthService.API_KEY_EXPIRY_KEY), Date.now(), API_KEY_EXPIRY_MARGIN_MS);
  }

  // One mint at a time. The mint endpoint is rate limited per user, and several views can discover
  // the same stale key within the same tick - without this they would stampede it and start getting
  // 429s, turning a recoverable state into a hard failure.
  private mintApiKey(): Promise<string | null> {
    if (!this.apiKeyMint) {
      this.apiKeyMint = this.requestApiKey().finally(() => { this.apiKeyMint = null; });
    }
    return this.apiKeyMint;
  }

  private async requestApiKey(): Promise<string | null> {
    // returnUserId falls back to localStorage; the in-memory field alone is blank after a reload,
    // which would post a uid-less body and earn a 400 instead of a key.
    const uid = this.returnUserId();
    const idToken = await this.getIdToken();
    if (!uid || !idToken) {
      // No signed-in session to mint against. Returning null here would be interpolated into an
      // `Authorization: Bearer null` header and come back as an unexplained 401, so send the user
      // to login instead - this is the "not even redirected to login" half of the bug.
      console.warn('[AuthService.returnApiKey] no signed-in session to mint an API key for');
      this.redirectToLogin();
      return null;
    }

    if (isPartnerLaunch()) {
      return this.requestPartnerSessionKey(idToken);
    }

    try {
      const response: any = await this.http.post(`${environment.CLOUD_URL_BASE}/api/generateApiKey`, {
        uid,
        readApplicants: true,
        pushApplicants: true,
        readJobOffers: true,
        pushJobOffers: true,
        readAgents: true,
        pushAgents: true,
        readData: true,
        pushData: true,
        executeAgents: true,
        executeWorkflow: true,
        viewWorkflow: true,
        manageWorkflow: true,
        readTools: true,
        manageTools: true,
        readMatching: true,
        createMatching: true,
        parsing: true
      }, { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` } }).toPromise();

      if (!response || !response.apiKey) {
        throw new Error('Failed to generate API key - no apiKey in response');
      }
      this.saveApiKey(response.apiKey, response.expiresAt);
      console.log('[AuthService.returnApiKey] API key generated, expires', response.expiresAt || 'unknown');
      return this.apiKey;
    } catch (e) {
      console.error('[AuthService.returnApiKey] Failed to generate API key:', e);
      // A rejected ID token means the session itself is gone, so login is the only way forward.
      // A 403 is NOT that: it means email-unverified or no payment method, and bouncing to login
      // would loop the user without ever explaining why.
      if (e?.status === 401) {
        this.redirectToLogin();
      }
      return null;
    }
  }

  private async requestPartnerSessionKey(idToken: string): Promise<string | null> {
    try {
      const response = await partnerEmbedRequest<{ apiKey: string; expiresAt: string }>(
        partnerApiBase(partnerLaunchEnv() || 'test'), '/key', { idToken }
      );
      this.saveApiKey(response.apiKey, response.expiresAt);
      return this.apiKey;
    } catch (e) {
      if (e?.status === 401 || e?.code === 'session_ended' || e?.code === 'tenant_not_active') {
        this.redirectToLogin();
      }
      return null;
    }
  }

  async loginPartnerSession(customToken: string): Promise<firebase.User | null> {
    await this.afAuth.setPersistence(firebase.auth.Auth.Persistence.NONE);
    const credential = await this.afAuth.signInWithCustomToken(customToken);
    return credential.user;
  }

  // sessionStorage, not localStorage: the login page clears localStorage on entry, and this is the
  // key login already reads back (login.page.ts getValidatedRedirectUrl).
  private redirectToLogin(): void {
    if (isPartnerLaunch()) {
      window.dispatchEvent(new CustomEvent(PARTNER_SESSION_ENDED_EVENT));
      return;
    }
    try {
      const current = this.router.url || '';
      if (current.startsWith('/login')) {
        return;
      }
      sessionStorage.setItem('redirectUrl', current);
    } catch { }
    this.router.navigate(['/login']);
  }

  hasApiKey(): boolean {
    return this.apiKey && this.apiKey.trim() !== '';
  }


  returnAccountId() {
    if (!this.accountId) {
      this.accountId = localStorage.getItem('accountId') || '';
    }
    return this.accountId;
  }




  returnUserRole() {
    return this.userRole;
  }

  signIn(credentials): Observable<any> {
    return from(this.afAuth.signInWithEmailAndPassword(credentials.email, credentials.password)).pipe(
      switchMap(user => {
        if (user) {
          this.isAuthenticatedSubject.next(true);
          return this.db.doc(`customers/${user.user.uid}`).valueChanges();
        } else {
          return of(null);
        }
      })
    )
  }

  signUp(credentials) {
    return this.afAuth.createUserWithEmailAndPassword(credentials.email, credentials.password).then(data => {
      return this.db.doc(`customers/${data.user.uid}`).set({
        name: credentials.name,
        email: credentials.email,
        created: firebase.firestore.FieldValue.serverTimestamp()
      });
    });
  }

  sendPasswordReset(email) {
    return this.afAuth.sendPasswordResetEmail(email);
  }

  async logOut() {
    // Clear all session data. clearApiKey also drops the PERSISTED key and its expiry - blanking the
    // in-memory field alone left the previous user's key in localStorage, valid server-side for the
    // rest of its lifetime, for whoever logged in next on a shared browser.
    this.clearApiKey();
    this.apiKey = '';
    this.userId = '';
    this.Useremail = '';
    this.userRole = '';
    this.customerNumber = 0;
    this.accountId = '';
    this.licenses = 0;
    this.userPlan.next('');
    this.isAuthenticatedSubject.next(false);
    this.clearPersistedAuth();

    await this.afAuth.signOut().then(() => {
      if (isPartnerLaunch()) {
        window.dispatchEvent(new CustomEvent(PARTNER_SESSION_ENDED_EVENT));
        return;
      }
      window.location.href = `${window.location.origin}/login`;
    });
  }

  isLoggedIn(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  setAuthenticated(value: boolean) {
    this.isAuthenticatedSubject.next(value);
  }

  updateUser(name) {
    return this.db.doc(`customers/${firebase.auth().currentUser.uid}`).update({
      name
    });
  }

  async loginUser(email: string, password: string): Promise<firebase.auth.UserCredential> {
    try {
      const isAuthenticated = await firebase.auth().signInWithEmailAndPassword(email, password);
      if (isAuthenticated) {
        this.isAuthenticatedSubject.next(true);
      }
      return isAuthenticated;
    } catch (error) {
      console.error('Firebase login error in AuthService:', error);
      // Re-throw the original Firebase error so it can be handled properly
      throw error;
    }
  }

  singInWithCustomeToken(token, secondApp) {
    if (!token || typeof token !== 'string') {
      throw new Error('Invalid custom token: must be a non-empty string');
    }
    return firebase.auth(secondApp).signInWithCustomToken(token);
  }

  updateIsFirstLoginFlag(id: string) {
    const userRef = this.db.collection('customers').doc(id);
    userRef.update({ isFirstLogin: false });
  }

  updateIsFirstLoginFlagToTrue(id: string) {
    const userRef = this.db.collection('customers').doc(id);
    userRef.update({ isFirstLogin: true });
  }

  async isFirstLogin(uid: string): Promise<boolean> {
    try {
      const userRef = this.db.collection('customers').doc(uid);
      const doc: any = await userRef.get().toPromise();

      return doc.data().isFirstLogin;
    } catch (error) {
      console.error('Error checking first login status:', error);
      return false;
    }
  }

  async signInWithSaml() {
    const provider = new firebase.auth.SAMLAuthProvider('saml.jumpcloud-demo');
    try {
      return this.afAuth.signInWithPopup(provider);
      // console.log('User:', result.user);
      // console.log('Profile:', result.additionalUserInfo?.profile);
    } catch (error) {
      console.error('SAML Login error:', error.code, error.message);
    }

  }

  // getIsFirstLoginFlag(uid: string) {
  //   const customerDoc = this.db.collection('customers').doc(uid);
  //   const isFirstLogin$ = customerDoc.get().pipe(
  //     switchMap(doc => {
  //       const data = doc.data();
  //       if (data.isFirstLogin == undefined) {
  //         return from(customerDoc.set({ isFirstLogin: true }, { merge: true })).pipe(
  //           map(() => true)
  //         );
  //       } else {
  //         return of(data.isFirstLogin);
  //       }
  //     })
  //   );
  //   return isFirstLogin$;
  // }

  resetPassword(email: string): Promise<void> {
    return firebase.auth().sendPasswordResetEmail(email);
  }

  logoutUser(): Promise<void> {
    return firebase.auth().signOut();
  }

  setPersistence() {
    // Use the AngularFireAuth instance instead of firebase.auth()
    return this.afAuth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);
  }


  verifySessionCookie(cookie) {
    this.isAuthenticatedSubject.next(cookie);
  }

  getLocalToken() {
    firebase.auth().currentUser?.getIdToken().then((token) => {
      const isHttps = window && window.location && window.location.protocol === 'https:';
      const secureAttr = isHttps ? '; Secure' : '';
      document.cookie = `firebaseAuthToken=${token}; path=/${secureAttr}; max-age=3600; SameSite=Strict`;
    });
  }

  signupUser(email: string, password: string, firstName?: string, lastName?: string): Promise<string> {
    return firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then((newUserCredential: firebase.auth.UserCredential) => {
        return newUserCredential.user.uid;
      })
      .catch(error => {
        console.error('Firebase signup error:', error);
        // Return error in a format that can be parsed by the signup component
        if (error.code) {
          return `Error: ${error.message} (${error.code})`;
        }
        return `Error: ${error.message || 'Unknown signup error occurred'}`;
      });
  }

  // signupUser(email: string, password: string, firstName?: string, lastName?: string): Promise<void> {
  //   return firebase
  //     .auth()
  //     .createUserWithEmailAndPassword(email, password)
  //     .then((newUserCredential: firebase.auth.UserCredential) => {
  //       firebase
  //         .firestore()
  //         .doc(`/customers/${newUserCredential.user.uid}`)
  //         .set(
  //           {
  //             email: email,
  //             firstName: firstName || "",
  //             lastName: lastName || "",
  //             created: new Date(),
  //             createdISO: new Date().toISOString().split("T")[0],
  //             createdISOTime: new Date().toISOString().split("T")[1],
  //             createdISODateTime: new Date().toISOString()
  //           }
  //         );
  //     })
  //     .catch(error => {
  //       console.error(error);
  //       throw new Error(error);
  //     });
  // }

  signInWithGoogle() {
    return this.afAuth
      .signInWithPopup(new firebase.auth.GoogleAuthProvider())
      .then((res) => {
        console.log('AuthService::Successful Google login', res);
        return this.ngZone.run(() => this.router.navigate(['/']));
      })
      .catch((err) => {
        console.log('AuthService::Failed Google login', err);
      });
  }

  // googleLogin(token: string): Observable<any> {
  //   return this.http.post<any>(`${this.baseUrl}/google-login`, { token });
  // }

  signInWithGithub() {
    return this.afAuth
      .signInWithPopup(new firebase.auth.GithubAuthProvider())
      .then((res) => {
        console.log('AuthService::Successful Github login', res);
        return this.ngZone.run(() => this.router.navigate(['/']));
      })
      .catch((err) => {
        console.log('AuthService::Failed Github login', err);
      });
  }

  setData(data: string) {

    this.data.next(data);
    data = "";
  }

  getData() {
    return this.data.asObservable();
  }


  getUserToken() {
    return this.returnUserId();
  }

  getApiKey() {
    return this.returnApiKey();
  }

  async loginWithApple() {
    const provider = new firebase.auth.OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');

    return this.afAuth.signInWithPopup(provider)
      .then((res) => {
        console.log('AuthService::Successful Apple login', res);
        this.isAuthenticatedSubject.next(true);
        return res;
      })
      .catch((err) => {
        console.log('AuthService::Failed Apple login', err);
        throw err;
      });
  }

  signInWithPopup(provider: firebase.auth.AuthProvider): Promise<firebase.auth.UserCredential> {
    return new Promise((resolve, reject) => {
      firebase.auth().signInWithPopup(provider)
        .then((result) => {
          console.log("Sign In Success", result);
          this.isAuthenticatedSubject.next(true);
          resolve(result);
        })
        .catch(async (error) => {
          if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-browser') {
            console.warn('[Auth] Popup blocked or closed, falling back to redirect');
            try {
              sessionStorage.setItem('pendingRedirectAuth', 'true');
              await firebase.auth().signInWithRedirect(provider);
            } catch (redirectError) {
              console.error('[Auth] Redirect fallback failed:', redirectError);
              reject(redirectError);
            }
            return;
          }

          if (error.code === 'auth/account-exists-with-different-credential') {
            const email = error.email;
            const result = await this.showPopUpPasswordAsk(email, error.credential);

            const password = result.data?.values?.password;

            return this.loginUser(email, password).then(
              async (user) => {
                if (user) {
                  console.log('User signed in successfully:', user);
                  const currentUser = await this.afAuth.currentUser;
                  console.log("currentUser", currentUser);
                  currentUser.linkWithCredential(error.credential).then((usercred) => {
                    const user = usercred;
                    this.isAuthenticatedSubject.next(true);
                    console.log("Account linking success", user);
                    resolve(user);
                  }).catch((error) => {
                    console.error("Account linking error", error);
                    reject(error);
                    return;
                  });
                } else {
                  console.error('Sign-in failed: No user returned');
                  reject(error);
                  return;
                }
              }).catch(
                (error) => {
                  console.error('Sign-in error:', error);
                  this.alertCtrl.create({
                    header: 'Login Failed',
                    message: 'Incorrect password. Please try again.',
                    buttons: ['OK']
                  }).then(alert => alert.present());
                }
              );
          }
          else {
            console.error("Error during sign in", error);
            reject(error);
          }
        });
    })
  }

  async checkRedirectResult(): Promise<firebase.auth.UserCredential | null> {
    if (sessionStorage.getItem('pendingRedirectAuth') !== 'true') return null;
    try {
      const result = await firebase.auth().getRedirectResult();
      sessionStorage.removeItem('pendingRedirectAuth');
      if (result?.user) {
        console.log('[Auth] Redirect sign-in success', result);
        this.isAuthenticatedSubject.next(true);
        return result;
      }
      return null;
    } catch (error: any) {
      sessionStorage.removeItem('pendingRedirectAuth');
      if (error.code === 'auth/account-exists-with-different-credential') {
        console.error('[Auth] Redirect: account exists with different credential', error);
      }
      throw error;
    }
  }

  async loginWithMicrosoft() {
    const microsoftProvider = new firebase.auth.OAuthProvider('microsoft.com');
    microsoftProvider.setCustomParameters({
      tenant: "common",
    })
    microsoftProvider.addScope('User.Read');
    microsoftProvider.addScope('openid');
    microsoftProvider.addScope('profile');

    return this.signInWithPopup(microsoftProvider);
  }

  async loginWithGmail() {
    const gmailProvider = new firebase.auth.GoogleAuthProvider;
    gmailProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
    gmailProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');
    gmailProvider.addScope('openid');
    gmailProvider.addScope('email');
    gmailProvider.addScope('profile');

    return this.signInWithPopup(gmailProvider)

  }

  setUserEmailToVerified(uid) {
    console.log("Setting user email to verified for UID:", uid);
    //call a cloud function to set the user as verified.
    this.http.post('https://europe-west3-team-front-end-912.cloudfunctions.net/setUserEmailToVerified', {
      uid: uid
    }).subscribe((response) => {
      console.log("Email verified successfully", response);
    }, (error) => {
      console.error("Error verifying email", error);
    });
  }

  saveUserRole(role: string) {
    localStorage.setItem('userRole', role);
    this.userRole = role;
  }

  saveUserId(uid: string) {
    localStorage.setItem('userId', uid);
    this.userId = uid;
  }

  // `expiresAt` is the ISO timestamp from the mint response. It is optional only because callers
  // outside the mint path exist; when it is absent the stored stamp is REMOVED rather than left
  // behind, otherwise a fresh key would inherit the previous key's expiry and be retired early -
  // or worse, a key with no stamp would silently keep an old, still-future one and never expire.
  saveApiKey(apiKey: string, expiresAt?: string) {
    const previous = localStorage.getItem(AuthService.API_KEY_KEY);
    const previousExpiry = localStorage.getItem(AuthService.API_KEY_EXPIRY_KEY);
    localStorage.setItem(AuthService.API_KEY_KEY, apiKey);
    this.apiKey = apiKey;

    const stamp = expiryToPersist(apiKey, previous, previousExpiry, expiresAt);
    if (stamp) {
      localStorage.setItem(AuthService.API_KEY_EXPIRY_KEY, stamp);
    } else {
      localStorage.removeItem(AuthService.API_KEY_EXPIRY_KEY);
    }
  }

  clearApiKey() {
    localStorage.removeItem(AuthService.API_KEY_KEY);
    localStorage.removeItem(AuthService.API_KEY_EXPIRY_KEY);
    this.apiKey = null;
    console.log('[AuthService] Cleared cached API key');
  }

  saveKundennummer(kundennummer: number) {
    localStorage.setItem('kundennummer', kundennummer.toString());
    this.customerNumber = kundennummer;
  }

  saveAccountId(accountId: string) {
    localStorage.setItem('accountId', accountId);
    this.accountId = accountId;
  }

  saveLicenses(licenses: any) {
    localStorage.setItem('licenses', JSON.stringify(licenses));
  }

  saveUserPlan(plan: string) {
    // A null/undefined plan would be persisted as the truthy string "null" and
    // every downstream plan check would then treat the customer as provisioned.
    const normalized = typeof plan === 'string' ? plan.trim() : '';
    if (normalized) {
      localStorage.setItem('userPlan', normalized);
    } else {
      localStorage.removeItem('userPlan');
    }
    this.userPlan.next(normalized);
  }

  getUserPlan(): string {
    return this.userPlan.getValue() || '';
  }

  setCustomTokenObj(customToken: any) {
    // Extract the actual token string from object if needed
    const tokenStr = typeof customToken === 'string'
      ? customToken
      : (customToken && customToken.customToken);

    if (tokenStr && typeof tokenStr === 'string') {
      this.customToken = tokenStr;
      localStorage.setItem('customerCustomToken', tokenStr);
    } else {
      console.warn('[Auth] Invalid custom token format:', customToken);
    }
  }

  async loginWithToken(token: string): Promise<void> {
    try {
      await this.afAuth.signInWithCustomToken(token);
      this.isAuthenticatedSubject.next(true);
    } catch (error) {
      console.error('Failed to sign in with custom token:', error);
      throw error;
    }
  }

  returnUserId(): string {
    if (!this.userId) {
      this.userId = localStorage.getItem('userId') || '';
    }
    return this.userId;
  }

  getUserRole(): string {
    return this.userRole || localStorage.getItem('userRole') || 'user';
  }

  returnCustomerNumber() {
    return this.customerNumber;
  }

  // async returnCustomerNumber(): Promise<number> {
  //   const user = firebase.auth().currentUser;
  //   if (user) {
  //     try {
  //       const doc = await this.db.collection('customers').doc(user.uid).get().toPromise();
  //       const data:any = doc.data();
  //       if(data && data.kundennummer != undefined && data.kundennummer >= 1) {
  //         return data.kundennummer;
  //       }
  //       return 0;
  //     } catch (error) {
  //       console.error('Error getting customer number:', error);
  //       return 0;
  //     }
  //   }
  //   return 0;
  // }

}
